import { create } from 'zustand';
import type { CityData, Track } from '../types/city';

interface GameState {
  // Player
  playerPosition: [number, number, number];
  playerRotation: number;
  setPlayerTransform: (pos: [number, number, number], rot: number) => void;

  // Audio
  activeTrackId: string | null;
  crossfadeLevel: number;
  nearbyBuildings: string[];
  setNearbyBuildings: (ids: string[]) => void;
  setActiveTrack: (id: string | null, level: number) => void;

  // Discovery
  discoveredTracks: Set<string>;
  discoverTrack: (id: string) => void;

  // UI
  modalTrackId: string | null;
  openModal: (id: string) => void;
  closeModal: () => void;

  // Artwork
  artworkMap: Map<string, string>;
  setArtwork: (map: Map<string, string>) => void;

  // Track lock (force-play a specific track)
  lockedTrackId: string | null;
  lockTrack: (id: string | null) => void;

  // City
  cityData: CityData | null;
  loadCity: (data: CityData) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

export const useGameStore = create<GameState>((set) => ({
  // Player defaults
  playerPosition: [0, 0.5, 8],
  playerRotation: 0,
  setPlayerTransform: (pos, rot) => set({ playerPosition: pos, playerRotation: rot }),

  // Audio defaults
  activeTrackId: null,
  crossfadeLevel: 0,
  nearbyBuildings: [],
  setNearbyBuildings: (ids) => set({ nearbyBuildings: ids }),
  setActiveTrack: (id, level) => set({ activeTrackId: id, crossfadeLevel: level }),

  // Discovery
  discoveredTracks: new Set(),
  discoverTrack: (id) =>
    set((state) => {
      const next = new Set(state.discoveredTracks);
      next.add(id);
      return { discoveredTracks: next };
    }),

  // UI
  modalTrackId: null,
  openModal: (id) => set({ modalTrackId: id }),
  closeModal: () => set({ modalTrackId: null }),

  // Artwork
  artworkMap: new Map(),
  setArtwork: (map) => set({ artworkMap: map }),

  // Track lock (toggle — clicking same building unlocks)
  lockedTrackId: null,
  lockTrack: (id) => set((state) => ({
    lockedTrackId: state.lockedTrackId === id ? null : id,
  })),

  // City
  cityData: null,
  loadCity: (data) => set({ cityData: data }),
  loading: true,
  setLoading: (loading) => set({ loading }),
}));
